
import './CopContacts.scss'

import { useEffect, useState } from 'react'

import { COP_COMMON_ERROR_MESSAGES } from '../../constants/common-error-messages'
import { ToastService } from '../../services/toast.service'
import { fetchAgreementDetails } from '../CopAgreementDetails/services/CopAgreementDetails.service'
import { useSectionPermissions } from '../Shared/PermissionAccessGuard/hooks/useSectionPermissions'

export type CopContactsProps = {
  type?: string
  title?: string
  phonetext?: string
  messagetext?: string
  errortext?: string
}

type ContactData = {
  firstName: string | null
  lastName: string | null
  phone: string | null
  email: string | null
  jobTitle: string | null
}

export default function CopContacts({
  type = 'Primary',
  title,
  phonetext = 'Tel.',
  messagetext = 'Send a message',
  errortext = 'Details not available',
}: CopContactsProps) {
  const [contactData, setContactData] = useState<ContactData | null>(null)
  const { permissions, isLoading } = useSectionPermissions()

  const typeNormalized = type?.toLowerCase()
  const titleNormalized = title?.toLowerCase()

  const isPrimary = !(titleNormalized === 'secondary contact' || typeNormalized === 'secondary')

  const defaultTitle = isPrimary ? 'Primary Contact' : 'Secondary Contact'
  const resolvedTitle = title || defaultTitle

  useEffect(() => {
    if (!isLoading && !permissions.canViewAgreementsViewContacts) return

    let cancelled = false

    fetchAgreementDetails()
      .then(response => {
        if (cancelled || !response) {
          return
        }

        const contact = isPrimary ? response.primaryContact : response.secondaryContact

        if (contact) {
          setContactData({
            firstName: contact.firstName || null,
            lastName: contact.lastName || null,
            phone: contact.phone || null,
            email: contact.email || null,
            jobTitle: contact.jobTitle || null,
          })
        }
      })
      .catch(err => {
        const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
          '{{action}}',
          COP_COMMON_ERROR_MESSAGES['api-error'].action['contacts-loading'],
        )
        const message = err instanceof Error && err.message ? err.message : fallbackMessage
        ToastService.error(message)
      })

    return () => {
      cancelled = true
    }
  }, [isPrimary, isLoading, permissions.canViewAgreementsViewContacts])

  const getInitials = (firstName: string | null, lastName: string | null) => {
    if (!firstName || !lastName) return '—'
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const isContactAvailable =
    contactData && (contactData.firstName || contactData.lastName || contactData.phone)

  const initials = contactData ? getInitials(contactData.firstName, contactData.lastName) : '—'
  const fullName =
    contactData?.firstName && contactData?.lastName
      ? `${contactData.firstName} ${contactData.lastName}`
      : null

  if (!isLoading && !permissions.canViewAgreementsViewContacts) return null

  return (
    <div className="cop-contacts">
      <h3 className="cop-contacts__title">{resolvedTitle}</h3>

      <div className="cop-contacts__card">
        {isContactAvailable && <div className="cop-contacts__avatar">{initials}</div>}

        <div className="cop-contacts__info">
          {!isContactAvailable ? (
            <div className="cop-contacts__not-available">{errortext}</div>
          ) : (
            <>
              {fullName && <div className="cop-contacts__name">{fullName}</div>}
              {contactData?.jobTitle && (
                <div className="cop-contacts__phone">
                  {contactData.jobTitle}
                </div>
              )}
              {contactData?.phone && (
                <div className="cop-contacts__phone">
                  {phonetext} {contactData.phone}
                </div>
              )}
              {contactData?.email && (
                <a href={`mailto:${contactData.email}`} className="cop-contacts__message">
                  {messagetext}
                </a>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}